import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import CardDetailModal from '../components/CardDetailModal';
import { useTheme } from '../contexts/ThemeContext';
import { 
  ArrowLeft, FileText, Send, Eye, CheckCircle, Clock, 
  Copy, ExternalLink, Mail, User, Calendar, Activity,
  BarChart2, Users, Check, AlertCircle, RefreshCw, Download,
  AlertTriangle, Building2, MapPin, StickyNote, XCircle, ClipboardList, Edit3
} from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { sanitizeHTML } from '../lib/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = '/qsecdef-logo.png';

// Helper to strip HTML tags
const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = sanitizeHTML(html);
  return tmp.textContent || tmp.innerText || '';
};

// Helper to format date for PDF
const formatPdfDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

// Helper to load image as base64
const loadImageAsBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = reject;
    img.src = url;
  });
};

const statusConfig = {
  draft: { label: 'Draft', color: 'text-gray-400', bg: 'bg-gray-500/20' },
  sent: { label: 'Sent', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  viewed: { label: 'Viewed', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  signed: { label: 'Signed', color: 'text-green-400', bg: 'bg-green-500/20' },
  expired: { label: 'Expired', color: 'text-red-400', bg: 'bg-red-500/20' }
};

const activityIcons = {
  created: FileText,
  sent: Send,
  viewed: Eye,
  section_viewed: BarChart2,
  signed: CheckCircle
};

const ProposalView = () => {
  const { proposalId } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [proposal, setProposal] = useState(null);
  const [activities, setActivities] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  
  // Card data for account info
  const [card, setCard] = useState(null);
  const [cardOwner, setCardOwner] = useState(null);
  const [cardNotes, setCardNotes] = useState([]);
  const [mediaProgress, setMediaProgress] = useState(null);
  
  // Modal state for viewing card
  const [showCardModal, setShowCardModal] = useState(false);
  const [cardModalTab, setCardModalTab] = useState('notes');
  
  // Resend email state
  const [resendingTo, setResendingTo] = useState(null);
  const [resendMessage, setResendMessage] = useState('');
  const [showResendModal, setShowResendModal] = useState(false);
  const [resendRecipient, setResendRecipient] = useState(null);
  
  // Email mismatch state
  const [cardContacts, setCardContacts] = useState([]);
  const [emailMismatch, setEmailMismatch] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [updateProposalEmail, setUpdateProposalEmail] = useState(true);

  useEffect(() => {
    fetchData();
  }, [proposalId]);

  const fetchData = async () => {
    try {
      const [proposalRes, activitiesRes, paymentsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/proposals/${proposalId}`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/proposals/${proposalId}/activities`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/payments/proposal/${proposalId}/transactions`, { credentials: 'include' })
      ]);
      
      if (proposalRes.ok) {
        const proposalData = await proposalRes.json();
        setProposal(proposalData);
        
        // Fetch card data if we have a card_id
        if (proposalData.card_id) {
          fetchCardData(proposalData.card_id);
        }
      }
      if (activitiesRes.ok) {
        setActivities(await activitiesRes.json());
      }
      if (paymentsRes.ok) {
        setPayments(await paymentsRes.json());
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCardData = async (cardId) => {
    try {
      const cardRes = await fetch(`${BACKEND_URL}/api/cards/${cardId}`, { credentials: 'include' });
      if (cardRes.ok) {
        const cardData = await cardRes.json();
        setCard(cardData);
        
        // Get last 5 notes (sort by date, newest first)
        const notes = cardData.notes || [];
        const sortedNotes = [...notes].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setCardNotes(sortedNotes.slice(0, 5));
        
        // Calculate media production progress
        if (cardData.columns) {
          const mediaColumns = cardData.columns.filter(c => c.board === 'media_production');
          const totalTasks = mediaColumns.reduce((sum, col) => sum + (col.items?.length || 0), 0);
          const completedTasks = mediaColumns
            .filter(c => c.name?.toLowerCase().includes('complete') || c.name?.toLowerCase().includes('done'))
            .reduce((sum, col) => sum + (col.items?.length || 0), 0);
          setMediaProgress({ completed: completedTasks, total: totalTasks });
        }
        
        // Fetch card owner info
        if (cardData.relationship_owner_id) {
          const usersRes = await fetch(`${BACKEND_URL}/api/users`, { credentials: 'include' });
          if (usersRes.ok) {
            const users = await usersRes.json();
            const owner = users.find(u => u.user_id === cardData.relationship_owner_id);
            setCardOwner(owner);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch card data:', error);
    }
  };

  // Open card modal to specific tab
  const openCardModal = (tab = 'notes') => {
    setCardModalTab(tab);
    setShowCardModal(true);
  };

  const copyLink = () => {
    const url = `${window.location.origin}/p/${proposal.access_token}`;
    navigator.clipboard.writeText(url);
    alert('Link copied to clipboard!');
  };

  const copyRecipientLink = (recipient) => {
    const url = `${window.location.origin}/p/${proposal.access_token}/r/${recipient.access_token}`;
    navigator.clipboard.writeText(url);
    alert(`Link for ${recipient.name} copied!`);
  };

  // Open resend modal for a specific recipient
  const openResendModal = async (recipient) => {
    setResendRecipient(recipient);
    setResendMessage(`Just following up on this proposal. Please let me know if you have any questions.`);
    setEmailMismatch(null);
    setSelectedEmail(recipient.email);
    setUpdateProposalEmail(true);
    
    // Fetch card contacts to check for email mismatch
    if (proposal?.card_id) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/cards/${proposal.card_id}`, {
          credentials: 'include'
        });
        if (response.ok) {
          const card = await response.json();
          const contacts = card.key_people || [];
          setCardContacts(contacts);
          
          // Check if recipient's email differs from card contact
          const recipientName = (recipient.name || '').toLowerCase().trim();
          const recipientEmail = (recipient.email || '').toLowerCase().trim();
          
          for (const contact of contacts) {
            const contactName = `${contact.first_name || ''} ${contact.last_name || ''}`.toLowerCase().trim();
            const contactEmail = (contact.email || '').toLowerCase().trim();
            
            // Match by name (fuzzy) - check if names are similar
            const nameMatch = recipientName && contactName && 
              (recipientName.includes(contactName) || contactName.includes(recipientName) ||
               recipientName === contactName);
            
            if (nameMatch && contactEmail && contactEmail !== recipientEmail) {
              setEmailMismatch({
                oldEmail: recipient.email,
                newEmail: contact.email,
                contactName: `${contact.first_name || ''} ${contact.last_name || ''}`.trim()
              });
              setSelectedEmail(contact.email); // Default to new email
              break;
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch card contacts:', error);
      }
    }
    
    setShowResendModal(true);
  };

  // Resend proposal email to recipient
  const handleResendProposal = async () => {
    if (!resendRecipient) return;
    
    const emailToUse = selectedEmail || resendRecipient.email;
    setResendingTo(emailToUse);
    
    try {
      // If email changed and user wants to update the proposal, do that first
      if (emailMismatch && updateProposalEmail && selectedEmail === emailMismatch.newEmail) {
        console.log('Updating proposal recipient email from', emailMismatch.oldEmail, 'to', emailMismatch.newEmail);
        
        // Update the recipient email in the proposal
        const updateResponse = await fetch(`${BACKEND_URL}/api/proposals/${proposalId}/recipients/${resendRecipient.recipient_id}/email`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ email: emailMismatch.newEmail })
        });
        
        if (!updateResponse.ok) {
          console.warn('Could not update proposal email, proceeding with send anyway');
        }
      }
      
      console.log('Resending to:', emailToUse, 'Proposal:', proposalId);
      const response = await fetch(`${BACKEND_URL}/api/proposals/${proposalId}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          recipient_email: emailToUse,
          cc_emails: [],
          subject: `Reminder: ${proposal.title}`,
          message: resendMessage
        })
      });

      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        const emailMsg = data._email_message || 'Proposal resent successfully';
        alert(emailMsg);
        setShowResendModal(false);
        setResendRecipient(null);
        setEmailMismatch(null);
        fetchData(); // Refresh to show updated activity
      } else {
        const error = await response.json();
        console.error('Send error response:', error);
        alert(error.detail || 'Failed to resend proposal');
      }
    } catch (error) {
      console.error('Resend error:', error);
      alert('Failed to resend proposal: ' + error.message);
    } finally {
      setResendingTo(null);
    }
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatDate(dateStr).split(',')[0];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  // PDF Download function with signature support
  const downloadPdf = async () => {
    if (!proposal) return;
    setGeneratingPdf(true);
    
    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let yPos = margin;
      let currentPage = 1;

      const darkGreen = [34, 197, 94];
      const darkText = [17, 17, 17];
      const grayText = [102, 102, 102];
      const lightGray = [245, 245, 245];

      const addPageBackground = () => {
        doc.setFillColor(255, 255, 255);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');
      };

      const addFooter = () => {
        doc.setFontSize(8);
        doc.setTextColor(...grayText);
        doc.text('QSECDEF - Membership Proposal', margin, pageHeight - 10);
        doc.text(`Page ${currentPage}`, pageWidth - margin - 15, pageHeight - 10);
      };

      const checkNewPage = (requiredSpace = 40) => {
        if (yPos > pageHeight - requiredSpace) {
          addFooter();
          doc.addPage();
          currentPage++;
          addPageBackground();
          yPos = margin;
          return true;
        }
        return false;
      };

      addPageBackground();

      // Try to load logo
      let logoBase64 = null;
      try {
        logoBase64 = await loadImageAsBase64(LOGO_URL);
      } catch (e) {
        console.log('Could not load logo');
      }

      // === HEADER SECTION ===
      if (logoBase64) {
        doc.addImage(logoBase64, 'PNG', margin, yPos, 30, 30);
        yPos += 38;
      } else {
        yPos += 5;
      }

      doc.setFontSize(24);
      doc.setTextColor(...darkText);
      doc.setFont('helvetica', 'bold');
      doc.text('Membership Proposal', margin, yPos);
      yPos += 12;

      doc.setFontSize(18);
      doc.setTextColor(...darkGreen);
      doc.text(proposal.client?.company_name || 'Client', margin, yPos);
      yPos += 10;

      doc.setFontSize(11);
      doc.setTextColor(...grayText);
      doc.setFont('helvetica', 'normal');
      const contactName = `${proposal.client?.main_contact_first_name || ''} ${proposal.client?.main_contact_last_name || ''}`.trim();
      doc.text(`Prepared for: ${contactName || 'Client'}`, margin, yPos);
      yPos += 6;
      doc.text(`Date: ${formatPdfDate(proposal.created_at)}`, margin, yPos);
      yPos += 8;

      doc.setDrawColor(...darkGreen);
      doc.setLineWidth(1);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 18;

      // === SECTIONS ===
      if (proposal.sections && proposal.sections.length > 0) {
        for (const section of proposal.sections) {
          checkNewPage(60);

          doc.setFillColor(...darkGreen);
          doc.rect(margin, yPos - 5, 3, 10, 'F');
          
          doc.setFontSize(14);
          doc.setTextColor(...darkText);
          doc.setFont('helvetica', 'bold');
          doc.text(section.title, margin + 8, yPos);
          yPos += 12;

          doc.setFontSize(10);
          doc.setTextColor(...grayText);
          doc.setFont('helvetica', 'normal');
          
          const plainContent = stripHtml(section.content);
          const lines = doc.splitTextToSize(plainContent, contentWidth - 8);
          
          for (const line of lines) {
            checkNewPage(15);
            doc.text(line, margin, yPos);
            yPos += 5.5;
          }
          
          yPos += 12;
        }
      }

      // === PRICING SECTION ===
      if (proposal.pricing) {
        checkNewPage(80);

        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 12;

        doc.setFontSize(16);
        doc.setTextColor(...darkText);
        doc.setFont('helvetica', 'bold');
        doc.text('Pricing Summary', margin, yPos);
        yPos += 14;

        if (proposal.pricing.pricing_options?.length > 0) {
          // For signed proposals, only show the selected option
          const optionsToShow = proposal.status === 'signed' && proposal.pricing.selected_option_id
            ? proposal.pricing.pricing_options.filter(opt => opt.option_id === proposal.pricing.selected_option_id)
            : proposal.pricing.pricing_options;

          for (const option of optionsToShow) {
            checkNewPage(70);

            doc.setFillColor(...lightGray);
            doc.rect(margin, yPos - 6, contentWidth, 12, 'F');
            doc.setFontSize(12);
            doc.setTextColor(...darkText);
            doc.setFont('helvetica', 'bold');
            doc.text(option.name + (proposal.status === 'signed' ? ' (Selected)' : ''), margin + 5, yPos);
            yPos += 14;

            doc.setFontSize(9);
            doc.setTextColor(...grayText);
            doc.setFont('helvetica', 'bold');
            doc.text('Item', margin, yPos);
            doc.text('Qty', margin + 95, yPos);
            doc.text('Price', margin + 115, yPos);
            doc.text('Total', margin + 145, yPos);
            yPos += 6;

            doc.setDrawColor(200, 200, 200);
            doc.line(margin, yPos, pageWidth - margin, yPos);
            yPos += 6;

            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...darkText);
            for (const item of option.line_items || []) {
              checkNewPage(15);
              const itemName = (item.product_name || '').substring(0, 45);
              doc.text(itemName, margin, yPos);
              doc.text(String(item.quantity || 1), margin + 95, yPos);
              doc.text(`£${(item.unit_price_gbp || 0).toLocaleString()}`, margin + 115, yPos);
              doc.text(`£${(item.total_gbp || 0).toLocaleString()}`, margin + 145, yPos);
              yPos += 7;
            }

            yPos += 5;
            doc.setDrawColor(200, 200, 200);
            doc.line(margin + 100, yPos - 3, pageWidth - margin, yPos - 3);
            doc.setFontSize(12);
            doc.setTextColor(...darkGreen);
            doc.setFont('helvetica', 'bold');
            doc.text(`Total: £${(option.total_gbp || 0).toLocaleString()}`, margin + 125, yPos + 3);
            yPos += 18;
          }
        } else if (proposal.pricing.line_items?.length > 0) {
          doc.setFontSize(9);
          doc.setTextColor(...grayText);
          doc.setFont('helvetica', 'bold');
          doc.text('Item', margin, yPos);
          doc.text('Qty', margin + 95, yPos);
          doc.text('Price', margin + 115, yPos);
          doc.text('Total', margin + 145, yPos);
          yPos += 6;

          doc.setDrawColor(200, 200, 200);
          doc.line(margin, yPos, pageWidth - margin, yPos);
          yPos += 6;

          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...darkText);
          for (const item of proposal.pricing.line_items) {
            checkNewPage(15);
            const itemName = (item.product_name || '').substring(0, 45);
            doc.text(itemName, margin, yPos);
            doc.text(String(item.quantity || 1), margin + 95, yPos);
            doc.text(`£${(item.unit_price_gbp || 0).toLocaleString()}`, margin + 115, yPos);
            doc.text(`£${(item.total_gbp || 0).toLocaleString()}`, margin + 145, yPos);
            yPos += 7;
          }

          yPos += 8;
          doc.setDrawColor(200, 200, 200);
          doc.line(margin + 100, yPos - 5, pageWidth - margin, yPos - 5);
          doc.setFontSize(10);
          doc.setTextColor(...darkText);
          doc.text(`Subtotal: £${(proposal.pricing.subtotal_gbp || 0).toLocaleString()}`, margin + 115, yPos);
          yPos += 6;
          if (proposal.pricing.charge_vat && proposal.pricing.tax_amount_gbp > 0) {
            doc.text(`VAT: £${(proposal.pricing.tax_amount_gbp || 0).toLocaleString()}`, margin + 115, yPos);
            yPos += 8;
          }
          doc.setFontSize(14);
          doc.setTextColor(...darkGreen);
          doc.setFont('helvetica', 'bold');
          doc.text(`Total: £${(proposal.pricing.total_gbp || 0).toLocaleString()}`, margin + 115, yPos);
        }
      }

      // === SIGNATURE SECTION ===
      // Show signature block for ALL proposals (signed shows signature, unsigned shows placeholder)
      checkNewPage(80);

      yPos += 15;
      doc.setDrawColor(...darkGreen);
      doc.setLineWidth(2);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 15;

      if (proposal.status === 'signed' && (proposal.signature || proposal.signatures?.length > 0)) {
        // SIGNED PROPOSAL - Show signature details
        doc.setFillColor(34, 197, 94);
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.roundedRect(margin, yPos - 8, 80, 12, 2, 2, 'F');
        doc.text('CONTRACT SIGNED', margin + 5, yPos);
        yPos += 15;

        const sig = proposal.signature || proposal.signatures?.[0];
        
        if (sig) {
          doc.setFontSize(11);
          doc.setTextColor(...darkText);
          doc.setFont('helvetica', 'normal');
          
          // Signer details
          doc.text(`Signed by: ${sig.signer_name || ''}`, margin, yPos);
          yPos += 7;
          
          if (sig.signer_title) {
            doc.text(`Title: ${sig.signer_title}`, margin, yPos);
            yPos += 7;
          }
          
          if (sig.signer_email) {
            doc.text(`Email: ${sig.signer_email}`, margin, yPos);
            yPos += 7;
          }
          
          doc.text(`Date: ${formatPdfDate(sig.signed_at)}`, margin, yPos);
          yPos += 12;
          
          // Add signature image if available
          if (sig.signature_data && sig.signature_data.startsWith('data:image')) {
            doc.setDrawColor(200, 200, 200);
            doc.setLineWidth(0.5);
            doc.rect(margin, yPos, 80, 35);
            doc.setFontSize(8);
            doc.setTextColor(...grayText);
            doc.text('Signature:', margin + 2, yPos + 5);
            
            try {
              doc.addImage(sig.signature_data, 'PNG', margin + 5, yPos + 8, 70, 22);
            } catch (e) {
              console.log('Could not add signature image');
              doc.setFontSize(10);
              doc.setTextColor(...darkText);
              doc.text('[Digital Signature on File]', margin + 10, yPos + 20);
            }
            yPos += 40;
          }

          // Legal statement
          yPos += 5;
          doc.setFontSize(8);
          doc.setTextColor(...grayText);
          const legalText = `This document was electronically signed on ${formatPdfDate(sig.signed_at)}. The signer confirmed their agreement to the terms and conditions of this proposal.`;
          const legalLines = doc.splitTextToSize(legalText, contentWidth);
          for (const line of legalLines) {
            doc.text(line, margin, yPos);
            yPos += 4;
          }
        }
      } else {
        // UNSIGNED PROPOSAL - Show signature placeholder
        doc.setFillColor(156, 163, 175); // Gray background
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.roundedRect(margin, yPos - 8, 100, 12, 2, 2, 'F');
        doc.text('AWAITING SIGNATURE', margin + 5, yPos);
        yPos += 20;

        // Client signature area
        doc.setFontSize(11);
        doc.setTextColor(...darkText);
        doc.setFont('helvetica', 'bold');
        doc.text('Client Acceptance', margin, yPos);
        yPos += 10;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(...grayText);
        doc.text('By signing below, the client agrees to the terms and conditions outlined in this proposal.', margin, yPos);
        yPos += 15;

        // Signature line
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.5);
        
        // Name field
        doc.text('Name:', margin, yPos);
        doc.line(margin + 20, yPos, margin + 90, yPos);
        yPos += 12;
        
        // Title field
        doc.text('Title:', margin, yPos);
        doc.line(margin + 20, yPos, margin + 90, yPos);
        yPos += 12;
        
        // Signature box
        doc.text('Signature:', margin, yPos);
        doc.rect(margin + 25, yPos - 5, 70, 25);
        yPos += 25;
        
        // Date field
        doc.text('Date:', margin, yPos);
        doc.line(margin + 20, yPos, margin + 60, yPos);
        yPos += 15;

        // Legal disclaimer
        doc.setFontSize(8);
        doc.setTextColor(...grayText);
        const disclaimerText = 'This proposal is valid until the expiry date shown above. To accept this proposal, please sign and return a copy or use the online signing portal if available.';
        const disclaimerLines = doc.splitTextToSize(disclaimerText, contentWidth);
        for (const line of disclaimerLines) {
          doc.text(line, margin, yPos);
          yPos += 4;
        }
      }

      addFooter();
      
      // Generate filename with status
      const statusSuffix = proposal.status === 'signed' ? '-SIGNED' : '';
      doc.save(`proposal-${proposal.client?.company_name || 'document'}${statusSuffix}.pdf`);

    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  if (loading) {
    return (
      <AppLayout title="Proposal">
        <div className="flex items-center justify-center h-64">
          <div className={`${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'} text-xl`}>Loading...</div>
        </div>
      </AppLayout>
    );
  }

  if (!proposal) {
    return (
      <AppLayout title="Proposal">
        <div className="text-center py-12">
          <h2 className={`text-xl ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>Proposal not found</h2>
          <button onClick={() => navigate('/proposals')} className={`${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'} hover:underline`}>
            Back to Proposals
          </button>
        </div>
      </AppLayout>
    );
  }

  const status = statusConfig[proposal.status] || statusConfig.draft;

  return (
    <AppLayout title="Proposal">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/proposals')}
              className={`p-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'} transition-colors`}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  {proposal.client?.company_name}
                </h1>
                <span className={`px-3 py-1 rounded text-sm font-medium ${status.bg} ${status.color}`}>
                  {status.label}
                </span>
              </div>
              <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'} mt-1`}>{proposal.title}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Edit Button - available for draft and sent proposals (not signed/expired) */}
            {proposal.status !== 'signed' && proposal.status !== 'expired' && (
              <button
                onClick={() => navigate(`/proposals/${proposal.proposal_id}/edit`)}
                className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#111] border-white/20 text-white hover:border-[#A2FF00]' : 'bg-white border-gray-300 text-gray-900 hover:border-emerald-500'} border rounded-sm transition-colors`}
              >
                <Edit3 className="w-4 h-4" />
                Edit
              </button>
            )}
            {/* Resend Button - available for sent proposals with recipients */}
            {proposal.status !== 'draft' && proposal.status !== 'expired' && proposal.recipients?.length > 0 && (
              <button
                onClick={() => {
                  // Open resend modal for first recipient (or user can select in Recipients section)
                  if (proposal.recipients && proposal.recipients[0]) {
                    openResendModal(proposal.recipients[0]);
                  }
                }}
                className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#111] border-white/20 text-white hover:border-blue-400' : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'} border rounded-sm transition-colors`}
              >
                <RefreshCw className="w-4 h-4" />
                Resend
              </button>
            )}
            <button
              onClick={downloadPdf}
              disabled={generatingPdf}
              className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#111] border-white/20 text-white hover:border-[#A2FF00]' : 'bg-white border-gray-300 text-gray-900 hover:border-emerald-500'} border rounded-sm transition-colors disabled:opacity-50`}
            >
              <Download className={`w-4 h-4 ${generatingPdf ? 'animate-pulse' : ''}`} />
              {generatingPdf ? 'Generating...' : 'Download PDF'}
            </button>
            <button
              onClick={copyLink}
              className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#111] border-white/20 text-white hover:border-[#A2FF00]' : 'bg-white border-gray-300 text-gray-900 hover:border-emerald-500'} border rounded-sm transition-colors`}
            >
              <Copy className="w-4 h-4" />
              Copy Link
            </button>
            <a
              href={`/p/${proposal.access_token}`}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center gap-2 px-4 py-2 ${isDark ? 'bg-[#A2FF00] hover:bg-[#8EFF00]' : 'bg-emerald-500 hover:bg-emerald-600'} text-black font-bold rounded-sm transition-colors`}
            >
              <ExternalLink className="w-4 h-4" />
              Open
            </a>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-5 gap-4">
              <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-4`}>
                <div className="text-gray-500 text-sm mb-1">Value</div>
                <div className={`text-xl font-bold ${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'}`}>
                  £{(proposal.pricing?.total_gbp || 0).toLocaleString()}
                </div>
              </div>
              <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-4`}>
                <div className="text-gray-500 text-sm mb-1">Recipients</div>
                <div className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {proposal.recipients?.filter(r => r.tracking?.open_count > 0).length || 0}
                  <span className="text-gray-500 text-sm font-normal">/{proposal.recipients?.length || 0}</span>
                </div>
              </div>
              <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-4`}>
                <div className="text-gray-500 text-sm mb-1">Total Opens</div>
                <div className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {proposal.recipients?.reduce((sum, r) => sum + (r.tracking?.open_count || 0), 0) || proposal.tracking?.open_count || 0}
                </div>
              </div>
              <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-4`}>
                <div className="text-gray-500 text-sm mb-1">View Time</div>
                <div className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {formatDuration(
                    proposal.recipients?.reduce((sum, r) => sum + (r.tracking?.time_spent_seconds || 0), 0) || 
                    proposal.tracking?.total_view_time_seconds || 0
                  )}
                </div>
              </div>
              <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-4`}>
                <div className="text-gray-500 text-sm mb-1">Expires</div>
                <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {formatDate(proposal.expires_at).split(',')[0]}
                </div>
              </div>
            </div>

            {/* Recipients Tracking */}
            {proposal.recipients && proposal.recipients.length > 0 && (
              <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                    <Users className={`w-5 h-5 ${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'}`} />
                    Recipients
                  </h3>
                  <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {proposal.recipients.filter(r => r.tracking?.open_count > 0).length} of {proposal.recipients.length} opened
                  </span>
                </div>
                
                <div className="space-y-4">
                  {proposal.recipients.map((recipient) => {
                    const hasOpened = recipient.tracking?.open_count > 0;
                    const sectionsViewed = recipient.tracking?.sections_viewed || [];
                    
                    return (
                      <div 
                        key={recipient.recipient_id}
                        className={`border rounded-sm p-4 ${
                          hasOpened 
                            ? 'border-green-500/30 bg-green-500/5' 
                            : isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              hasOpened ? 'bg-green-500/20' : 'bg-gray-500/20'
                            }`}>
                              {hasOpened ? (
                                <Check className="w-4 h-4 text-green-400" />
                              ) : (
                                <Clock className="w-4 h-4 text-gray-400" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium`}>{recipient.name}</span>
                                <span className={`text-xs text-gray-500 ${isDark ? 'bg-white/10' : 'bg-gray-200'} px-2 py-0.5 rounded`}>
                                  {recipient.role}
                                </span>
                              </div>
                              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{recipient.email}</div>
                              
                              {/* Tracking Stats */}
                              {hasOpened ? (
                                <div className="mt-2 space-y-1">
                                  <div className="flex items-center gap-4 text-sm">
                                    <span className="text-green-400 flex items-center gap-1">
                                      <Eye className="w-3 h-3" />
                                      Opened {recipient.tracking.open_count}x
                                    </span>
                                    <span className="text-gray-400">
                                      Last: {formatTimeAgo(recipient.tracking.last_opened_at)}
                                    </span>
                                    <span className="text-gray-400">
                                      Time: {formatDuration(recipient.tracking.time_spent_seconds)}
                                    </span>
                                  </div>
                                  
                                  {/* Sections Viewed */}
                                  {proposal.sections && proposal.sections.length > 0 && (
                                    <div className="flex items-center gap-1 flex-wrap mt-2">
                                      <span className="text-xs text-gray-500 mr-1">Sections:</span>
                                      {proposal.sections.map((section) => {
                                        const viewed = sectionsViewed.includes(section.section_id);
                                        return (
                                          <span 
                                            key={section.section_id}
                                            className={`text-xs px-2 py-0.5 rounded ${
                                              viewed 
                                                ? 'bg-green-500/20 text-green-400' 
                                                : 'bg-gray-500/20 text-gray-500'
                                            }`}
                                            title={viewed ? 'Viewed' : 'Not viewed'}
                                          >
                                            {viewed && <Check className="w-2 h-2 inline mr-1" />}
                                            {section.title}
                                          </span>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="mt-2 text-sm text-gray-500 flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" />
                                  Not opened yet
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => copyRecipientLink(recipient)}
                              className="p-2 text-gray-400 hover:text-[#A2FF00] hover:bg-white/5 rounded transition-colors"
                              title="Copy unique link"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openResendModal(recipient)}
                              disabled={resendingTo === recipient.email}
                              className={`p-2 text-gray-400 hover:text-blue-400 hover:bg-white/5 rounded transition-colors ${
                                resendingTo === recipient.email ? 'opacity-50 cursor-wait' : ''
                              }`}
                              title="Resend proposal email"
                            >
                              <RefreshCw className={`w-4 h-4 ${resendingTo === recipient.email ? 'animate-spin' : ''}`} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Section Analytics */}
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6`}>
              <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>Section Engagement</h3>
              <div className="space-y-3">
                {proposal.sections?.map((section) => {
                  const analytics = proposal.tracking?.section_analytics?.find(
                    s => s.section_id === section.section_id
                  );
                  const maxTime = Math.max(
                    ...proposal.tracking?.section_analytics?.map(s => s.total_time_seconds || 0) || [1]
                  );
                  const percentage = analytics ? (analytics.total_time_seconds / maxTime) * 100 : 0;
                  
                  return (
                    <div key={section.section_id}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{section.title}</span>
                        <span className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {analytics?.views || 0} views • {formatDuration(analytics?.total_time_seconds)}
                        </span>
                      </div>
                      <div className={`h-2 ${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-200'} rounded-full overflow-hidden`}>
                        <div 
                          className={`h-full ${isDark ? 'bg-[#A2FF00]' : 'bg-emerald-500'} transition-all`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Engagement Summary - Simplified view per recipient */}
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6`}>
              <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-4 flex items-center gap-2`}>
                <Users className="w-5 h-5" />
                Engagement Summary
              </h3>
              {!proposal.recipients || proposal.recipients.length === 0 ? (
                <p className="text-gray-500">No recipients yet</p>
              ) : (
                <div className="space-y-4">
                  {proposal.recipients.map((recipient) => {
                    const tracking = recipient.tracking || {};
                    const viewCount = tracking.view_count || 0;
                    const lastViewed = tracking.last_viewed;
                    
                    // Calculate sections viewed from recipient tracking OR from proposal-level section_analytics
                    const recipientSectionsViewed = tracking.sections_viewed || [];
                    const proposalSectionAnalytics = proposal.tracking?.section_analytics || [];
                    
                    // Use recipient-level tracking if available, otherwise calculate from section_analytics
                    const sectionsViewed = recipientSectionsViewed.length > 0 
                      ? recipientSectionsViewed.length
                      : proposalSectionAnalytics.filter(s => s.views > 0).length;
                    
                    const totalSections = proposal.sections?.length || 0;
                    
                    // Get list of viewed section IDs for display
                    const viewedSectionIds = recipientSectionsViewed.length > 0
                      ? recipientSectionsViewed
                      : proposalSectionAnalytics.filter(s => s.views > 0).map(s => s.section_id);
                    
                    return (
                      <div key={recipient.recipient_id} className={`p-3 rounded-sm ${isDark ? 'bg-[#0A0A0A] border-white/5' : 'bg-gray-50 border-gray-100'} border`}>
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-8 h-8 ${viewCount > 0 ? (isDark ? 'bg-[#A2FF00]/20' : 'bg-emerald-100') : (isDark ? 'bg-gray-700' : 'bg-gray-200')} rounded-full flex items-center justify-center`}>
                            <User className={`w-4 h-4 ${viewCount > 0 ? (isDark ? 'text-[#A2FF00]' : 'text-emerald-600') : 'text-gray-500'}`} />
                          </div>
                          <div className="flex-1">
                            <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{recipient.name}</div>
                            <div className="text-gray-500 text-xs">{recipient.email}</div>
                          </div>
                          {viewCount > 0 && (
                            <span className={`text-xs px-2 py-1 rounded ${isDark ? 'bg-[#A2FF00]/10 text-[#A2FF00]' : 'bg-emerald-100 text-emerald-600'}`}>
                              Active
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-sm mb-2">
                          <div className="text-center">
                            <div className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{viewCount}</div>
                            <div className="text-gray-500 text-xs">Views</div>
                          </div>
                          <div className="text-center">
                            <div className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{sectionsViewed}/{totalSections}</div>
                            <div className="text-gray-500 text-xs">Sections</div>
                          </div>
                          <div className="text-center">
                            <div className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'} text-xs`}>
                              {lastViewed ? formatDate(lastViewed) : 'Never'}
                            </div>
                            <div className="text-gray-500 text-xs">Last View</div>
                          </div>
                        </div>
                        {/* Sections Viewed List */}
                        {sectionsViewed > 0 && proposal.sections && (
                          <div className="mt-2 pt-2 border-t border-white/5">
                            <div className="text-xs text-gray-500 mb-1">Pages viewed:</div>
                            <div className="flex flex-wrap gap-1">
                              {proposal.sections.map((section) => {
                                const viewed = viewedSectionIds.includes(section.section_id);
                                return (
                                  <span 
                                    key={section.section_id}
                                    className={`text-xs px-1.5 py-0.5 rounded ${
                                      viewed 
                                        ? isDark ? 'bg-[#A2FF00]/20 text-[#A2FF00]' : 'bg-emerald-100 text-emerald-600'
                                        : isDark ? 'bg-gray-700/50 text-gray-500' : 'bg-gray-200 text-gray-400'
                                    }`}
                                  >
                                    {section.title}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Account Information */}
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6`}>
              <h3 className={`text-sm font-bold ${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'} mb-4 flex items-center gap-2`}>
                <Building2 className="w-4 h-4" />
                ACCOUNT INFO
              </h3>
              <div className="space-y-3">
                {/* Account Owner */}
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-gray-500" />
                  <div>
                    <div className="text-xs text-gray-500">Account Owner</div>
                    <div className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium`}>
                      {cardOwner?.name || 'Unassigned'}
                    </div>
                  </div>
                </div>
                
                {/* Created Date */}
                <div className="flex items-center gap-3">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <div>
                    <div className="text-xs text-gray-500">Created</div>
                    <div className={`${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {card?.created_at ? formatDate(card.created_at) : formatDate(proposal?.created_at)}
                    </div>
                  </div>
                </div>
                
                {/* Country */}
                {card?.country && (
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-gray-500" />
                    <div>
                      <div className="text-xs text-gray-500">Country</div>
                      <div className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{card.country}</div>
                    </div>
                  </div>
                )}
                
                {/* Media Production Progress (for signed proposals) */}
                {mediaProgress && mediaProgress.total > 0 && (
                  <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <ClipboardList className={`w-4 h-4 ${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'}`} />
                      <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Production Progress</span>
                    </div>
                    <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
                      {mediaProgress.completed} of {mediaProgress.total} completed ({Math.round((mediaProgress.completed / mediaProgress.total) * 100)}%)
                    </div>
                    <div className={`h-2 ${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-200'} rounded-full overflow-hidden`}>
                      <div 
                        className={`h-full ${isDark ? 'bg-[#A2FF00]' : 'bg-emerald-500'} transition-all`}
                        style={{ width: `${(mediaProgress.completed / mediaProgress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
                
                {/* Decline Reason (if declined) */}
                {proposal?.status === 'declined' && proposal?.decline_info && (
                  <div className={`mt-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span className="text-sm font-medium text-red-500">Decline Reason</span>
                    </div>
                    <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {proposal.decline_info.decline_reason || 'No reason provided'}
                    </div>
                    {proposal.decline_info.declined_by_name && (
                      <div className="text-xs text-gray-500 mt-1">
                        Declined by {proposal.decline_info.declined_by_name}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Recent Notes */}
            {cardNotes.length > 0 && (
              <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6`}>
                <h3 className={`text-sm font-bold ${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'} mb-4 flex items-center justify-between`}>
                  <span className="flex items-center gap-2">
                    <StickyNote className="w-4 h-4" />
                    RECENT NOTES
                  </span>
                  <button
                    onClick={() => openCardModal('notes')}
                    className={`text-xs ${isDark ? 'text-gray-400 hover:text-[#A2FF00]' : 'text-gray-500 hover:text-emerald-600'} transition-colors`}
                  >
                    View All →
                  </button>
                </h3>
                <div className="space-y-3">
                  {cardNotes.map((note, idx) => (
                    <div 
                      key={note.note_id || idx}
                      onClick={() => openCardModal('notes')}
                      className={`p-3 rounded-sm ${isDark ? 'bg-[#0A0A0A] hover:bg-white/5' : 'bg-gray-50 hover:bg-gray-100'} cursor-pointer transition-colors`}
                    >
                      <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'} line-clamp-2`}>
                        {note.content}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {note.created_by_name || 'Unknown'} • {formatDate(note.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Client Info */}
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6`}>
              <h3 className={`text-sm font-bold ${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'} mb-4`}>CLIENT</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${isDark ? 'bg-[#A2FF00]/20' : 'bg-emerald-100'} rounded-full flex items-center justify-center`}>
                    <User className={`w-5 h-5 ${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'}`} />
                  </div>
                  <div>
                    <div className={`${isDark ? 'text-white' : 'text-gray-900'} font-medium`}>
                      {proposal.client?.main_contact_first_name} {proposal.client?.main_contact_last_name}
                    </div>
                    <div className="text-gray-500 text-sm">{proposal.client?.main_contact_title}</div>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-500" />
                    <a href={`mailto:${proposal.client?.main_contact_email}`} className={`${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'} hover:underline`}>
                      {proposal.client?.main_contact_email}
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-500" />
                    <button
                      onClick={() => navigate(`/member-tracker?card=${proposal.card_id}`)}
                      className={`${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'} hover:underline flex items-center gap-1`}
                    >
                      {proposal.client?.company_name}
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Signature Info */}
            {proposal.signature && (
              <div className={`${isDark ? 'bg-[#111]' : 'bg-white'} border border-green-500/30 rounded-sm p-6`}>
                <h3 className="text-sm font-bold text-green-400 mb-4 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  SIGNED
                </h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-500">Signed by:</span>
                    <span className={`${isDark ? 'text-white' : 'text-gray-900'} ml-2`}>{proposal.signature.signer_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <span className={`${isDark ? 'text-white' : 'text-gray-900'} ml-2`}>{proposal.signature.signer_email}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Date:</span>
                    <span className={`${isDark ? 'text-white' : 'text-gray-900'} ml-2`}>{formatDate(proposal.signature.signed_at)}</span>
                  </div>
                </div>
                {proposal.signature.signature_data && (
                  <div className={`mt-4 ${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-100'} rounded p-2`}>
                    <img 
                      src={proposal.signature.signature_data} 
                      alt="Signature" 
                      className="w-full h-auto"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Pricing Summary */}
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6`}>
              <h3 className={`text-sm font-bold ${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'} mb-4`}>PRICING</h3>
              <div className="space-y-2 text-sm">
                {proposal.pricing?.line_items?.map((item, index) => (
                  <div key={index} className="flex justify-between">
                    <span className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{item.product_name}</span>
                    <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>£{item.total_gbp.toLocaleString()}</span>
                  </div>
                ))}
                <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} pt-2 mt-2`}>
                  <div className="flex justify-between">
                    <span className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Subtotal</span>
                    <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>£{(proposal.pricing?.subtotal_gbp || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>VAT</span>
                    <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>£{(proposal.pricing?.tax_amount_gbp || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2">
                    <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>Total</span>
                    <span className={`${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'}`}>£{(proposal.pricing?.total_gbp || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment History */}
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6`}>
              <h3 className={`text-sm font-bold ${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'} mb-4`}>PAYMENT HISTORY</h3>
              {payments.length === 0 ? (
                <div className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'} text-center py-4`}>
                  No payments recorded
                </div>
              ) : (
                <div className="space-y-3">
                  {payments.map((payment, index) => {
                    const isComplete = ['complete', 'completed', 'paid'].includes(payment.status);
                    const isPending = payment.status === 'pending';
                    return (
                      <div 
                        key={index} 
                        className={`p-3 rounded-sm ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-2">
                              {isComplete ? (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              ) : isPending ? (
                                <Clock className="w-4 h-4 text-yellow-500" />
                              ) : (
                                <AlertCircle className="w-4 h-4 text-red-500" />
                              )}
                              <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                £{(payment.amount || 0).toLocaleString()}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                isComplete 
                                  ? 'bg-green-500/20 text-green-400' 
                                  : isPending 
                                    ? 'bg-yellow-500/20 text-yellow-400'
                                    : 'bg-red-500/20 text-red-400'
                              }`}>
                                {payment.status}
                              </span>
                            </div>
                            <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'} mt-1`}>
                              {payment.payment_method && (
                                <span className="capitalize">{payment.payment_method.replace('_', ' ')} • </span>
                              )}
                              {payment.provider && (
                                <span className="capitalize">{payment.provider} • </span>
                              )}
                              {formatDate(payment.created_at)}
                            </div>
                          </div>
                        </div>
                        {payment.stripe_payment_intent_id && (
                          <div className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'} mt-2 font-mono`}>
                            Ref: {payment.stripe_payment_intent_id.slice(-12)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {/* Payment Summary */}
                  {payments.some(p => ['complete', 'completed', 'paid'].includes(p.status)) && (
                    <div className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'} pt-3 mt-3`}>
                      <div className="flex justify-between text-sm">
                        <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Total Paid</span>
                        <span className="text-green-500 font-bold">
                          £{payments
                            .filter(p => ['complete', 'completed', 'paid'].includes(p.status))
                            .reduce((sum, p) => sum + (p.amount || 0), 0)
                            .toLocaleString()}
                        </span>
                      </div>
                      {proposal.pricing?.total_gbp > 0 && (
                        <div className="flex justify-between text-sm mt-1">
                          <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Outstanding</span>
                          <span className={`font-bold ${
                            (proposal.pricing.total_gbp - payments
                              .filter(p => ['complete', 'completed', 'paid'].includes(p.status))
                              .reduce((sum, p) => sum + (p.amount || 0), 0)) <= 0
                              ? 'text-green-500'
                              : isDark ? 'text-orange-400' : 'text-orange-500'
                          }`}>
                            £{Math.max(0, proposal.pricing.total_gbp - payments
                              .filter(p => ['complete', 'completed', 'paid'].includes(p.status))
                              .reduce((sum, p) => sum + (p.amount || 0), 0))
                              .toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Dates */}
            <div className={`${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6`}>
              <h3 className={`text-sm font-bold ${isDark ? 'text-[#A2FF00]' : 'text-emerald-600'} mb-4`}>DATES</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-500" />
                  <span className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Created:</span>
                  <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{formatDate(proposal.created_at)}</span>
                </div>
                {proposal.tracking?.sent_at && (
                  <div className="flex items-center gap-2">
                    <Send className="w-4 h-4 text-gray-500" />
                    <span className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Sent:</span>
                    <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{formatDate(proposal.tracking.sent_at)}</span>
                  </div>
                )}
                {proposal.tracking?.first_opened_at && (
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-gray-500" />
                    <span className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>First opened:</span>
                    <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{formatDate(proposal.tracking.first_opened_at)}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Expires:</span>
                  <span className={`${isDark ? 'text-white' : 'text-gray-900'}`}>{formatDate(proposal.expires_at)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Resend Proposal Modal */}
      {showResendModal && resendRecipient && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/20' : 'bg-white border-gray-200'} border rounded-sm w-full max-w-md`}>
            <div className={`p-4 border-b ${isDark ? 'border-white/20 bg-[#111]' : 'border-gray-200 bg-gray-50'}`}>
              <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                <RefreshCw className="w-5 h-5 text-blue-400" />
                Resend Proposal
              </h2>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Email Mismatch Warning */}
              {emailMismatch && (
                <div className={`p-3 rounded-sm border ${isDark ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-yellow-50 border-yellow-200'}`}>
                  <div className="flex items-start gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className={`font-medium ${isDark ? 'text-yellow-400' : 'text-yellow-700'}`}>
                        Email address has changed
                      </p>
                      <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        The contact&apos;s email on the card differs from this proposal.
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 ml-7">
                    <label className={`flex items-center gap-3 p-2 rounded cursor-pointer ${
                      selectedEmail === emailMismatch.newEmail 
                        ? isDark ? 'bg-green-500/20 border border-green-500/30' : 'bg-green-50 border border-green-200'
                        : isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                    }`}>
                      <input
                        type="radio"
                        name="emailChoice"
                        checked={selectedEmail === emailMismatch.newEmail}
                        onChange={() => setSelectedEmail(emailMismatch.newEmail)}
                        className="text-green-500"
                      />
                      <div className="flex-1">
                        <span className={`text-sm font-medium ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                          New email (recommended)
                        </span>
                        <span className={`block text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {emailMismatch.newEmail}
                        </span>
                      </div>
                    </label>
                    
                    <label className={`flex items-center gap-3 p-2 rounded cursor-pointer ${
                      selectedEmail === emailMismatch.oldEmail 
                        ? isDark ? 'bg-white/10 border border-white/20' : 'bg-gray-100 border border-gray-200'
                        : isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                    }`}>
                      <input
                        type="radio"
                        name="emailChoice"
                        checked={selectedEmail === emailMismatch.oldEmail}
                        onChange={() => setSelectedEmail(emailMismatch.oldEmail)}
                        className="text-gray-500"
                      />
                      <div className="flex-1">
                        <span className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          Original email
                        </span>
                        <span className={`block text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {emailMismatch.oldEmail}
                        </span>
                      </div>
                    </label>
                  </div>
                  
                  {selectedEmail === emailMismatch.newEmail && (
                    <label className={`flex items-center gap-2 mt-3 ml-7 text-sm cursor-pointer ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      <input
                        type="checkbox"
                        checked={updateProposalEmail}
                        onChange={(e) => setUpdateProposalEmail(e.target.checked)}
                        className="rounded text-green-500"
                      />
                      Update proposal with new email
                    </label>
                  )}
                </div>
              )}
              
              <div>
                <label className={`block text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>To</label>
                <div className={`px-3 py-2 rounded-sm ${isDark ? 'bg-[#111] text-white' : 'bg-gray-100 text-gray-900'}`}>
                  {resendRecipient.name || resendRecipient.email}
                  <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'} ml-2`}>
                    ({emailMismatch ? selectedEmail : resendRecipient.email})
                  </span>
                </div>
              </div>
              
              <div>
                <label className={`block text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Subject</label>
                <div className={`px-3 py-2 rounded-sm ${isDark ? 'bg-[#111] text-white' : 'bg-gray-100 text-gray-900'}`}>
                  Reminder: {proposal.title}
                </div>
              </div>
              
              <div>
                <label className={`block text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`}>Message</label>
                <textarea
                  value={resendMessage}
                  onChange={(e) => setResendMessage(e.target.value)}
                  rows={4}
                  className={`w-full px-3 py-2 rounded-sm border outline-none focus:ring-2 focus:ring-blue-500/50 ${
                    isDark 
                      ? 'bg-[#111] border-white/20 text-white placeholder-gray-500' 
                      : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
                  }`}
                  placeholder="Add a personal message..."
                />
              </div>
            </div>
            
            <div className={`p-4 border-t ${isDark ? 'border-white/20' : 'border-gray-200'} flex justify-end gap-3`}>
              <button
                onClick={() => { setShowResendModal(false); setResendRecipient(null); setEmailMismatch(null); }}
                className={`px-4 py-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors`}
              >
                Cancel
              </button>
              <button
                onClick={handleResendProposal}
                disabled={resendingTo}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-sm transition-colors disabled:opacity-50"
              >
                {resendingTo ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Resend Proposal
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Card Detail Modal */}
      {showCardModal && card && (
        <CardDetailModal
          card={card}
          isOpen={showCardModal}
          onClose={() => setShowCardModal(false)}
          onUpdate={(updatedCard) => {
            setCard(updatedCard);
            // Refresh notes
            const notes = updatedCard.notes || [];
            const sortedNotes = [...notes].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            setCardNotes(sortedNotes.slice(0, 5));
          }}
          boardType="member_tracker"
          defaultTab={cardModalTab}
        />
      )}
    </AppLayout>
  );
};

export default ProposalView;
